import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { hubApi } from '../../lib/api/hub';
import {
  ago,
  authorStatusLabel,
  crmFeedbackApi,
  eventWhat,
  feedbackFileUrl,
  fmtDT,
  statusOf,
  type FeedbackEvent,
  type MyTicket,
} from '../../lib/feedback';
import { authorQuestionMeta } from '../../lib/support-format';
import { IconExternalLink, IconPaperclip, IconSend } from './feedback-icons';
import { KindIcon, Pill, StatusPill } from './feedback-ui';

const CRM_URL = 'https://crm.brasol.ru';

/** «Мои обращения» CRM-пользователя: статусы и ответы команды. */
export function MineList({ onOpen, onNew }: { onOpen: (id: string) => void; onNew: (k: 'bug' | 'idea') => void }): JSX.Element {
  const q = useQuery({ queryKey: ['wiki', 'feedback', 'my'], queryFn: crmFeedbackApi.my, staleTime: 15_000 });
  const rows: MyTicket[] = q.data ?? [];
  return (
    <div className="fk-hub-fb-pad">
      {q.isLoading ? <div className="fk-hub-fb-empty">Загружаем…</div> : null}
      {q.isError ? <div className="fk-hub-fb-empty">Не удалось загрузить обращения. Попробуйте позже.</div> : null}
      {!q.isLoading && !q.isError && !rows.length ? <div className="fk-hub-fb-empty">Обращений пока нет. Нашли ошибку или есть идея — расскажите!</div> : null}
      {rows.map((r) => {
        // Вопрос в УК: отдел, кто взял, до какого часа ответ.
        const qm = authorQuestionMeta(r);
        return (
          <button type="button" key={r.id} className="fk-hub-fb-item" onClick={() => onOpen(r.id)}>
            <KindIcon kind={r.kind} />
            <span className="fk-hub-fb-item-main">
              <span className="fk-hub-fb-item-title">{r.title}</span>
              <span className="fk-hub-fb-item-meta">
                <span className="fk-hub-fb-mono">{r.code}</span>
                <span>{ago(r.updatedAt)}</span>
                {!r.isMine && r.authorName ? <span>· {r.authorName}</span> : null}
                {qm.parts.map((p) => (
                  <span key={p}>· {p}</span>
                ))}
                {qm.urgent ? <span className="fk-hub-fb-urgent">· срочно</span> : null}
              </span>
            </span>
            {r.authorUnread ? <span className="fk-hub-fb-unread" title="есть новый ответ" /> : null}
            <StatusPill status={r.status} label={authorStatusLabel(r.status)} />
          </button>
        );
      })}
      <div className="fk-hub-fb-new">
        <button type="button" className="fk-hub-fb-new-btn" onClick={() => onNew('bug')}>
          + Сообщить об ошибке
        </button>
        <button type="button" className="fk-hub-fb-new-btn" onClick={() => onNew('idea')}>
          + Предложить идею
        </button>
      </div>
    </div>
  );
}

function Timeline({ events, ticketKind }: { events: FeedbackEvent[]; ticketKind: MyTicket['kind'] }): JSX.Element {
  return (
    <div className="fk-hub-fb-tl">
      {events.map((ev) => {
        const tone = ev.type === 'status' ? statusOf(ev.toStatus ?? '').tone : ev.type === 'reply' ? 'var(--fk-orange)' : ev.type === 'verify_ok' ? 'var(--fk-success-text)' : ev.type === 'verify_fail' ? 'var(--fk-error)' : 'var(--fk-border-strong)';
        return (
          <div key={ev.id} className="fk-hub-fb-tl-item">
            <span className="fk-hub-fb-tl-dot" style={{ background: tone }} />
            <div className="fk-hub-fb-tl-head">
              <b>{ev.type === 'system' ? 'CRM' : (ev.authorName ?? 'Система')}</b>
              <span>{eventWhat(ev, ticketKind)}</span>
              {ev.internal ? <Pill tone="var(--fk-warning-text)">для команды</Pill> : null}
              <span className="fk-hub-fb-tl-date">{fmtDT(ev.createdAt)}</span>
            </div>
            {ev.text ? (
              <div className="fk-hub-fb-bubble" data-reply={ev.type === 'reply' ? 'true' : 'false'}>
                {ev.type === 'status' && ev.toStatus === 'rejected' ? <b>Причина: </b> : null}
                {ev.text}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Карточка обращения: лента, вложения и поле «Дополнить или ответить команде». */
export function MineDetail({ id, full, onChanged }: { id: string; full: boolean; onChanged: () => void }): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['wiki', 'feedback', 'my', id], queryFn: () => crmFeedbackApi.myOne(id) });
  const [text, setText] = useState('');
  const comment = useMutation({
    mutationFn: () => crmFeedbackApi.comment(id, text.trim()),
    onSuccess: () => {
      setText('');
      message.success('Комментарий отправлен команде');
      void qc.invalidateQueries({ queryKey: ['wiki', 'feedback'] });
      onChanged();
    },
    onError: (e: Error) => message.error(e.message),
  });

  // Отметка «прочитано» ставится сервером при открытии — счётчик на кнопке обновим.
  useEffect(() => {
    if (q.data) onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.id]);

  if (q.isLoading) return <div className="fk-hub-fb-empty">Загружаем…</div>;
  if (!q.data) return <div className="fk-hub-fb-empty">Обращение не найдено</div>;
  const t = q.data;
  const qm = authorQuestionMeta(t);
  return (
    <div className="fk-hub-fb-pad fk-hub-fb-detail">
      <div>
        <div className="fk-hub-fb-detail-top">
          <span className="fk-hub-fb-mono fk-hub-fb-muted">{t.code}</span>
          <StatusPill status={t.status} label={authorStatusLabel(t.status)} />
        </div>
        <div className="fk-hub-fb-detail-title">{t.title}</div>
        {t.status === 'rejected' && t.rejectReason ? <div className="fk-hub-fb-sub">Причина: {t.rejectReason}</div> : null}
        {qm.parts.length || qm.urgent ? (
          <div className="fk-hub-fb-sub">
            {qm.parts.join(' · ')}
            {qm.urgent ? <span className="fk-hub-fb-urgent">{qm.parts.length ? ' · ' : ''}срочно</span> : null}
          </div>
        ) : null}
      </div>

      {t.status === 'verify' ? (
        <div className="fk-hub-fb-verify">
          <b>Мы исправили — проверьте, пожалуйста</b>
          <span>Подтвердить, что всё работает, можно в CRM.</span>
          <a className="fk-hub-fb-link" href={`${CRM_URL}/profile?feedback=${encodeURIComponent(t.id)}`} target="_blank" rel="noopener noreferrer">
            Открыть в CRM <IconExternalLink size={12} />
          </a>
        </div>
      ) : null}

      <div className="fk-hub-fb-cols" data-full={full ? 'true' : 'false'}>
        <div>
          {t.expected ? (
            <div className="fk-hub-fb-sub fk-hub-fb-gap-b">
              <b>Ожидали:</b> {t.expected}
            </div>
          ) : null}
          <Timeline events={t.events} ticketKind={t.kind} />
        </div>
        <div>
          <textarea
            className="fk-hub-fb-input fk-hub-fb-textarea fk-hub-fb-textarea--sm"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Дополнить или ответить команде… (Ctrl+Enter)"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && text.trim() && !comment.isPending) comment.mutate();
            }}
            maxLength={3000}
          />
          <div className="fk-hub-fb-right">
            <button type="button" className="fk-hub-fb-send fk-hub-fb-send--sm" disabled={!text.trim() || comment.isPending} onClick={() => comment.mutate()}>
              <IconSend size={13} />
              Отправить
            </button>
          </div>
          {t.attachments.length ? (
            <div className="fk-hub-fb-gap">
              <div className="fk-hub-fb-label">Вложения</div>
              {t.attachments.map((a) => (
                <a key={a.id} className="fk-hub-fb-attach" href={feedbackFileUrl(t.id, a.id)} target="_blank" rel="noopener noreferrer">
                  <IconPaperclip size={13} />
                  <span>{a.filename}</span>
                </a>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** «Мои обращения» сотрудника iiko: статус и последний ответ УК (карточки и комментариев у прокси HUB нет). */
export function IikoMineList({ onNew }: { onNew: (k: 'bug' | 'idea') => void }): JSX.Element {
  const q = useQuery({ queryKey: ['wiki', 'hub', 'feedback', 'mine'], queryFn: () => hubApi.myFeedback(), staleTime: 15_000 });
  const items = q.data ?? [];
  return (
    <div className="fk-hub-fb-pad">
      {q.isLoading ? <div className="fk-hub-fb-empty">Загружаем…</div> : null}
      {q.isError ? <div className="fk-hub-fb-empty">Не удалось загрузить обращения. Попробуйте позже.</div> : null}
      {!q.isLoading && !q.isError && !items.length ? <div className="fk-hub-fb-empty">Вы ещё не писали в УК. Нашли ошибку или есть идея — расскажите!</div> : null}
      {items.map((t) => (
        <div key={t.id} className="fk-hub-fb-item fk-hub-fb-item--static">
          <KindIcon kind={t.kind} />
          <span className="fk-hub-fb-item-main">
            <span className="fk-hub-fb-item-title">{t.title}</span>
            <span className="fk-hub-fb-item-meta">
              <span className="fk-hub-fb-mono">{t.code}</span>
              <span>{ago(t.updatedAt)}</span>
            </span>
            {t.lastReply ? (
              <span className="fk-hub-fb-reply">
                <b>{t.lastReply.authorName || 'УК'}:</b> {t.lastReply.text}
              </span>
            ) : null}
            {t.status === 'rejected' && t.rejectReason ? <span className="fk-hub-fb-reply">Причина: {t.rejectReason}</span> : null}
          </span>
          <StatusPill status={t.status} label={t.statusLabel} />
        </div>
      ))}
      <div className="fk-hub-fb-new">
        <button type="button" className="fk-hub-fb-new-btn" onClick={() => onNew('bug')}>
          + Сообщить об ошибке
        </button>
        <button type="button" className="fk-hub-fb-new-btn" onClick={() => onNew('idea')}>
          + Предложить идею
        </button>
      </div>
    </div>
  );
}
